import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import type { TodoTask } from '../../types/todo';

type LinkTaskPickerProps = {
  // 「挂到谁下面」的那个谁。候选过滤以它为基准（后端的 linkable_under）。
  parentId: number;
  onPick: (childId: number) => void;
};

// 「挂一条已有任务」。读自己发 fetch —— DetailPane 的全受控约定管的是写，
// 一次只读的候选查询不必绕一圈从 props 传进来。选中之后把 id 交给调用方，
// 由它去写那一条的 parent_id。
const LinkTaskPicker: React.FC<LinkTaskPickerProps> = ({ parentId, onPick }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<TodoTask[]>([]);

  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (!term) {
      setRows([]);
      return;
    }
    // 防抖 200ms：这个框每敲一个字都会打一次后端，而 TaskSearchQuery 是
    // ILIKE 全表扫（它自己的头注释里写着这一点）。
    let cancelled = false;
    const timer = setTimeout(async () => {
      const result = await api.tasks.linkable(term, parentId);
      if (!cancelled) setRows(result?.tasks ?? []);
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, q, parentId]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-[9px] px-[13px] pb-[9px] pt-1 text-left text-[13px] text-todo-muted"
      >
        <span className="text-[14px]">⇱</span>
        挂一条已有任务
      </button>
    );
  }

  return (
    <div className="px-[13px] pb-[9px] pt-1">
      <input
        autoFocus
        value={q}
        onChange={(event) => setQ(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setQ('');
            setOpen(false);
          }
        }}
        placeholder="搜标题，选一条挂到这条任务下面"
        className="w-full border-0 bg-transparent text-[13px] text-todo-ink outline-none placeholder:text-todo-muted"
      />
      {rows.map((row) => (
        <button
          key={row.id}
          type="button"
          onClick={() => {
            onPick(row.id);
            setQ('');
            setOpen(false);
          }}
          className="block w-full truncate py-[5px] text-left text-[13px] text-todo-ink2 hover:text-todo-accent"
        >
          {/* 已经挂在别人下面的候选也留着 —— 转挂是合法的（新父亲是根，造不出
              三层），面包屑把它当下的状态说清楚就够了。后端的 LINKABLE 刻意
              不过滤这一类。 */}
          {row.parent?.title && <span className="text-todo-muted">{row.parent.title} › </span>}
          {row.title}
        </button>
      ))}
      {q.trim() && rows.length === 0 && (
        <div className="py-[5px] text-[13px] text-todo-muted">没有能挂的任务</div>
      )}
    </div>
  );
};

export default LinkTaskPicker;
